/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React, { useState, useEffect } from 'react';
import { DropdownMenu, IconButton } from '@radix-ui/themes';
import { User, UserCircle, Key, LogOut } from 'lucide-react';
import { ExtJS, useIsVisible, useTheme } from '@sonatype/nexus-ui-plugin';
import { useRouter, useSref } from '@uirouter/react';
import useHasUser from '../../hooks/useHasUser';
import { ROUTE_NAMES } from '../../routerConfig/routeNames/routeNames';
import UIStrings from '../../constants/UIStrings';

export default function LoginAndUserButton() {
  const hasUser = useHasUser();
  const router = useRouter();

  if (hasUser) {
    return <ProfileMenu />;
  } else {
    return (
      <IconButton
        variant="ghost"
        title="Log In"
        aria-label="Log In"
        onClick={onSignInClick}
      >
        <User size={18} />
      </IconButton>
    );
  }

  function onSignInClick() {
    // Keep original requested URL and then encode to Base64
    const url = router.urlService.url();
    console.log('[CloudHeader] Sign in clicked, returnTo:', url);
    if (url) {
      const returnTo = btoa(`#${url}`);
      router.stateService.go(ROUTE_NAMES.LOGIN, { returnTo });
    } else {
      router.stateService.go(ROUTE_NAMES.LOGIN);
    }
  }
}

function ProfileMenu() {
  const { USER } = ROUTE_NAMES;
  const { setTheme } = useTheme();

  const userTokenRouteName = USER.USER_TOKEN;
  const userNugetApiTokenName = USER.NUGETAPITOKEN;

  // Use regular useState instead of ExtJS.useState to avoid hooks issues during logout
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    try {
      const state = ExtJS.state();
      if (state) {
        setUserId(state.getValue('user')?.id || null);
      }
    } catch (e) {
      setUserId(null);
    }
  }, []);

  const router = useRouter();

  const userTokenRouteState = router.stateRegistry.get(userTokenRouteName);
  const isUserTokenEnabled = useIsVisible(userTokenRouteState?.data?.visibilityRequirements);

  const userNugetApiTokenState = router.stateRegistry.get(userNugetApiTokenName);
  const isUserNugetApiTokenEnabled = useIsVisible(userNugetApiTokenState?.data?.visibilityRequirements);

  // Preview vs default UI follows the hash (same idea as self-hosted GlobalHeaderRadix), so
  // the user menu does not send Preview users to #user/account, #user/usertoken, etc.
  const [hashPath, setHashPath] = useState(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.location.hash.replace(/^#/, '').split('?')[0] || '';
  });
  useEffect(() => {
    const syncFromLocation = () => {
      setHashPath(window.location.hash.replace(/^#/, '').split('?')[0] || '');
    };
    syncFromLocation();
    window.addEventListener('hashchange', syncFromLocation);
    window.addEventListener('popstate', syncFromLocation);
    return () => {
      window.removeEventListener('hashchange', syncFromLocation);
      window.removeEventListener('popstate', syncFromLocation);
    };
  }, []);
  const isPreviewUI = hashPath === 'preview' || hashPath.startsWith('preview/');

  const userAccountState = isPreviewUI ? 'preview.user.account' : USER.ACCOUNT;
  const nugetApiTokenState = isPreviewUI ? 'preview.user.nugetapitoken' : USER.NUGETAPITOKEN;
  const userTokenState = isPreviewUI ? 'preview.user.usertoken' : userTokenRouteName;
  const { href: userAccountHref } = useSref(userAccountState);
  const { href: userNugetApiTokenHref } = useSref(nugetApiTokenState);
  const { href: userTokenHref } = useSref(userTokenState);

  function onSignOutClick() {
    console.log('[CloudHeader] Sign out clicked, userId:', userId);
    // Reset to light mode on logout since Default UI doesn't support dark mode
    setTheme('light');
    ExtJS.signOut();
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton
          variant="ghost"
          title="Account settings, tokens and keys"
          aria-label="Account settings, tokens and keys"
        >
          <UserCircle size={18} />
        </IconButton>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="end">
        <DropdownMenu.Label>{userId}</DropdownMenu.Label>
        <DropdownMenu.Separator />

        <DropdownMenu.Item asChild>
          <a href={userAccountHref}>
            <User size={14} style={{ marginRight: '8px' }} />
            {UIStrings.USER_ACCOUNT.MENU.text}
          </a>
        </DropdownMenu.Item>

        {isUserNugetApiTokenEnabled && (
          <DropdownMenu.Item asChild>
            <a href={userNugetApiTokenHref}>
              <Key size={14} style={{ marginRight: '8px' }} />
              NuGet API Key
            </a>
          </DropdownMenu.Item>
        )}

        {isUserTokenEnabled && (
          <DropdownMenu.Item asChild>
            <a href={userTokenHref}>
              <Key size={14} style={{ marginRight: '8px' }} />
              User Token
            </a>
          </DropdownMenu.Item>
        )}

        <DropdownMenu.Separator />

        <DropdownMenu.Item onClick={onSignOutClick} color="red">
          <LogOut size={14} style={{ marginRight: '8px' }} />
          Log Out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
